import type { FC, ReactNode } from 'preact/compat';
import { useMemo, useState, useRef, useCallback } from 'preact/compat';

import { COUNTRIES, DEFAULT_COUNTRY } from '../../components/InputPhone/lib/constants';
import type { Country } from '../../components/InputPhone/model';

import { PhoneContext } from './PhoneContext';

interface Props {
  children: ReactNode;
}

export const PhoneProvider: FC<Props> = ({ children }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    COUNTRIES.find((c) => c.code === DEFAULT_COUNTRY) || COUNTRIES[0]
  );
  const onPhoneSubmitRef = useRef<(() => void) | undefined>(undefined);
  const onPhoneCancelRef = useRef<(() => void) | undefined>(undefined);

  const setOnPhoneSubmit = useCallback((callback: (() => void) | undefined) => {
    onPhoneSubmitRef.current = callback;
  }, []);

  const setOnPhoneCancel = useCallback((callback: (() => void) | undefined) => {
    onPhoneCancelRef.current = callback;
  }, []);

  const onPhoneSubmit = useCallback(() => {
    onPhoneSubmitRef.current?.();
  }, []);

  const onPhoneCancel = useCallback(() => {
    onPhoneCancelRef.current?.();
  }, []);

  const showPhoneInputRef = useRef<(() => void) | undefined>(undefined);

  const setShowPhoneInput = useCallback((callback: (() => void) | undefined) => {
    showPhoneInputRef.current = callback;
  }, []);

  const showPhoneInput = useCallback(() => {
    showPhoneInputRef.current?.();
  }, []);

  const contextValue = useMemo(
    () => ({
      phoneNumber,
      setPhoneNumber,
      selectedCountry,
      setSelectedCountry,
      onPhoneSubmit,
      setOnPhoneSubmit,
      onPhoneCancel,
      setOnPhoneCancel,
      setShowPhoneInput,
      showPhoneInput,
    }),
    [
      phoneNumber,
      selectedCountry,
      onPhoneSubmit,
      setOnPhoneSubmit,
      onPhoneCancel,
      setOnPhoneCancel,
      setShowPhoneInput,
      showPhoneInput,
    ]
  );

  return <PhoneContext.Provider value={contextValue}>{children}</PhoneContext.Provider>;
};
